import type React from 'react';
import { cn } from '../../../../../../../../../lib/utils';
import type { FullReportType, StandardCheck, StandardReportDetailData } from './types';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatDisplayDate = (value?: string | null): string => {
  if (!value) return '-';
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!isoMatch) return value;

  const [, year, month, day] = isoMatch;
  return `${day}-${MONTH_LABELS[Number(month) - 1] || month}-${year}`;
};

export const statusNoteClassName = (status?: string) => cn(
  'rounded-md px-2 py-1 text-[11px]',
  status === 'FAIL' && 'bg-[rgba(196,0,31,0.06)] text-readable-red',
  status === 'PENDING_REVIEW' && 'bg-[rgba(230,161,0,0.10)] text-[#8A6500]',
  status !== 'FAIL' && status !== 'PENDING_REVIEW' && 'bg-page-bg text-heritage-grey'
);

export const parseMaxFromLimitText = (limit?: string): number | undefined => {
  if (!limit) return undefined;
  const maxMatch = /(?:<=|≤)\s*(-?\d+(?:\.\d+)?)/.exec(limit);
  if (maxMatch) return Number(maxMatch[1]);
  const rangeMatch = /-?\d+(?:\.\d+)?\s*-\s*(-?\d+(?:\.\d+)?)/.exec(limit);
  if (rangeMatch) return Number(rangeMatch[1]);
  return undefined;
};

export const getStandardReportSummaryStats = (
  reportType: FullReportType,
  detail: StandardReportDetailData,
  passCount: number,
  issueCount: number
): Array<{ label: string; value: React.ReactNode; sub?: string }> => {
  if (reportType === 'VISUAL') {
    return [
      { label: 'Surface', value: '0', sub: 'rejectable' },
      { label: 'End prep', value: 'OK', sub: 'bevel/end face' },
      { label: 'Marking', value: 'OK', sub: 'trace readable' },
    ];
  }

  if (reportType === 'THREAD') {
    return [
      { label: 'End type', value: 'Bevelled', sub: 'project' },
      { label: 'Threading', value: 'No', sub: 'required' },
      { label: 'Impact', value: 'N/A', sub: 'not required' },
    ];
  }

  if (reportType === 'PIPE_BODY_UT') {
    return [
      { label: 'Coverage', value: '100%', sub: 'scan' },
      { label: 'Min WT', value: '22.1 mm', sub: 'verified' },
      { label: 'Rejects', value: '0', sub: 'laminations' },
    ];
  }

  if (reportType === 'COATING') {
    return [
      { label: 'Transfer', value: 'No', sub: 'awaiting' },
      { label: 'DFT min', value: '0 um', sub: 'not received' },
      { label: 'Holiday', value: '0', sub: 'pending' },
    ];
  }

  if (reportType === 'WELD_MAP') {
    return [
      { label: 'WPS', value: 'Linked', sub: 'approved' },
      { label: 'Seam', value: '12.18 m', sub: 'mapped' },
      { label: 'Repairs', value: '0', sub: 'open' },
    ];
  }

  return [
    { label: 'Checks', value: detail.checks.length, sub: 'configured' },
    { label: 'Passing', value: passCount, sub: 'checks' },
    { label: 'Issues', value: issueCount, sub: 'open' },
  ];
};

export const standardLimitText = (check: StandardCheck) => {
  if (check.min != null && check.max != null) return `${check.min}-${check.max} ${check.unit}`;
  if (check.min != null) return `>= ${check.min} ${check.unit}`;
  if (check.max != null) return `<= ${check.max} ${check.unit}`;
  return 'informational';
};

export const standardScaleMin = (check: StandardCheck) => {
  if (check.min != null) return Math.max(0, check.min - Math.max(1, Math.abs(check.min * 0.1)));
  return 0;
};

export const standardScaleMax = (check: StandardCheck) => {
  if (check.max != null) return check.max + Math.max(1, Math.abs(check.max * 0.2));
  return Math.max(1, check.value * 1.2);
};

export const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;
